import React, { useEffect, useState } from 'react';
import { getDisplayedQuantity, isProductRemoved } from '../../lib/changeRequestDraft';
import { fontSans, railBg } from './requestChangesStyles';

const stepperButtonStyle = {
  width: 28,
  height: 28,
  minWidth: 28,
  minHeight: 28,
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.4)',
  color: '#fff',
  fontSize: 15,
  fontFamily: fontSans,
  cursor: 'pointer',
  padding: 0,
  lineHeight: 1,
  flexShrink: 0
};

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true" focusable="false">
      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 11v6M14 11v6" strokeLinecap="round" />
    </svg>
  );
}

function sanitizeQuantity(raw, fallback) {
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '') return { valid: false, value: fallback };
  if (!/^\d+$/.test(trimmed)) return { valid: false, value: fallback };
  return { valid: true, value: parseInt(trimmed, 10) };
}

export default function RequestChangesProductControls({
  product,
  sectionName,
  sectionIdx,
  productIdx,
  change,
  onQuantityChange,
  onRemove,
  onUndo
}) {
  const originalQuantity = parseInt(product?.quantity, 10) || 0;
  const currentQuantity = getDisplayedQuantity(product, change);
  const removed = isProductRemoved(change);
  const quantityChanged = !removed && !!change && currentQuantity !== originalQuantity;
  const [hovered, setHovered] = useState(null);
  const [draftValue, setDraftValue] = useState(String(currentQuantity));

  useEffect(() => {
    // Keep the last real quantity while removed so Undo never flashes "0".
    if (removed) {
      setDraftValue(String(originalQuantity));
      return;
    }
    setDraftValue(String(currentQuantity));
  }, [currentQuantity, removed, originalQuantity]);

  const payload = {
    sectionIdx,
    productIdx,
    originalQuantity,
    productName: product?.name || '',
    sectionName: sectionName || ''
  };

  const commitQuantity = (raw) => {
    const { valid, value } = sanitizeQuantity(raw, currentQuantity);
    if (!valid) {
      setDraftValue(String(currentQuantity));
      return;
    }
    setDraftValue(String(value));
    if (value === 0) {
      onRemove(payload);
      return;
    }
    onQuantityChange({ ...payload, newQuantity: value });
  };

  if (removed) {
    return (
      <div
        className="no-print request-changes-editing-rail"
        role="group"
        aria-label={`${product?.name || 'Product'} requested for removal`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          width: '100%',
          minHeight: 52,
          padding: '0 12px',
          flexWrap: 'nowrap',
          backgroundColor: railBg,
          color: '#fff',
          boxSizing: 'border-box'
        }}
      >
        <span style={{
          fontSize: 10,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          fontFamily: fontSans,
          fontWeight: 500,
          color: '#fff'
        }}>
          Requested for removal
        </span>
        <button
          type="button"
          onClick={() => onUndo(sectionIdx, productIdx)}
          aria-label={`Undo removal request for ${product?.name || 'product'}`}
          style={{
            background: 'none',
            border: 'none',
            color: '#fff',
            fontSize: 13,
            fontFamily: fontSans,
            textDecoration: 'underline',
            textUnderlineOffset: 3,
            cursor: 'pointer',
            padding: '8px 0',
            minHeight: 40
          }}
        >
          Undo
        </button>
      </div>
    );
  }

  const productLabel = product?.name || 'product';

  return (
    <div
      className="no-print request-changes-editing-rail"
      role="group"
      aria-label={`Edit requested quantity for ${productLabel}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 8,
        width: '100%',
        minHeight: 52,
        padding: '6px 12px',
        backgroundColor: railBg,
        color: '#fff',
        boxSizing: 'border-box'
      }}
    >
      <div
        className="request-changes-rail-left"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          minWidth: 0,
          minHeight: 44
        }}
      >
        <label
          htmlFor={`request-qty-${sectionIdx}-${productIdx}`}
          className="request-changes-rail-secondary"
          style={{
            fontSize: 9,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            fontFamily: fontSans,
            color: 'rgba(255,255,255,0.72)',
            marginRight: 6,
            flexShrink: 0
          }}
        >
          Quantity
        </label>
        <div
          className="request-changes-stepper"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            minHeight: 44,
            flexShrink: 0
          }}
        >
          <button
            type="button"
            aria-label={`Decrease quantity of ${productLabel}`}
            onClick={() => commitQuantity(currentQuantity - 1)}
            onMouseEnter={() => setHovered('minus')}
            onMouseLeave={() => setHovered(null)}
            style={{
              ...stepperButtonStyle,
              backgroundColor: hovered === 'minus' ? 'rgba(255,255,255,0.14)' : 'transparent'
            }}
          >
            −
          </button>
          <input
            id={`request-qty-${sectionIdx}-${productIdx}`}
            className="request-changes-qty-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            aria-label={`Requested quantity for ${productLabel}`}
            value={draftValue}
            onChange={(e) => {
              const next = e.target.value.replace(/[^\d]/g, '');
              setDraftValue(next);
            }}
            onFocus={(e) => e.target.select()}
            onBlur={() => commitQuantity(draftValue)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitQuantity(draftValue);
                e.currentTarget.blur();
              }
              if (e.key === 'Escape') {
                setDraftValue(String(currentQuantity));
                e.currentTarget.blur();
              }
            }}
            style={{
              width: 32,
              height: 26,
              padding: 0,
              border: 'none',
              borderRadius: 2,
              backgroundColor: '#FFFFFF',
              color: '#2C2C2C',
              fontSize: 13,
              fontWeight: 400,
              fontFamily: fontSans,
              lineHeight: '26px',
              textAlign: 'center',
              outline: 'none',
              boxSizing: 'border-box',
              WebkitAppearance: 'none',
              appearance: 'none'
            }}
          />
          <button
            type="button"
            aria-label={`Increase quantity of ${productLabel}`}
            onClick={() => commitQuantity(currentQuantity + 1)}
            onMouseEnter={() => setHovered('plus')}
            onMouseLeave={() => setHovered(null)}
            style={{
              ...stepperButtonStyle,
              backgroundColor: hovered === 'plus' ? 'rgba(255,255,255,0.14)' : 'transparent'
            }}
          >
            +
          </button>
        </div>
        {quantityChanged && (
          <span className="request-changes-rail-secondary" style={{
            fontSize: 11,
            fontFamily: fontSans,
            color: 'rgba(255,255,255,0.7)',
            marginLeft: 6,
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}>
            was {originalQuantity}
          </span>
        )}
      </div>
      <button
        type="button"
        className="request-changes-remove"
        title="Remove"
        aria-label={`Remove ${productLabel}`}
        onClick={() => onRemove(payload)}
        onMouseEnter={() => setHovered('remove')}
        onMouseLeave={() => setHovered(null)}
        style={{
          minHeight: 32,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 5,
          background: 'none',
          border: 'none',
          color: hovered === 'remove' ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.88)',
          cursor: 'pointer',
          padding: '4px 2px',
          marginLeft: 'auto',
          flexShrink: 0
        }}
      >
        <TrashIcon />
        <span
          className="request-changes-remove-label"
          style={{
            fontSize: 11,
            fontWeight: 400,
            fontFamily: fontSans,
            letterSpacing: '0.02em',
            lineHeight: 1,
            whiteSpace: 'nowrap'
          }}
        >
          Remove
        </span>
      </button>
    </div>
  );
}
